import { Container, Point, Rectangle } from "pixi.js"
import { TileSize } from "game-common/models"

interface TileGroup {
    container: Container
    cells: Map<string, Rectangle[]>
    left: number
    top: number
    right: number
    bottom: number
}

/** Rebuilt with map artwork; each frame queries only cells touched by the player's pose. */
export class RenderedTileOcclusion {
    private groups = new Map<Container, TileGroup>()
    private topLeft = new Point()
    private bottomRight = new Point()
    private localTopLeft = new Point()
    private localBottomRight = new Point()

    clear(): void { this.groups.clear() }

    add(container: Container, bounds: Rectangle): void {
        let group = this.groups.get(container)
        if (!group) {
            group = { container, cells: new Map(), left: Infinity, top: Infinity, right: -Infinity, bottom: -Infinity }
            this.groups.set(container, group)
        }
        group.left = Math.min(group.left, bounds.x)
        group.top = Math.min(group.top, bounds.y)
        group.right = Math.max(group.right, bounds.right)
        group.bottom = Math.max(group.bottom, bounds.bottom)
        for (let x = Math.floor(bounds.x / TileSize); x < Math.ceil(bounds.right / TileSize); x++) {
            for (let y = Math.floor(bounds.y / TileSize); y < Math.ceil(bounds.bottom / TileSize); y++) {
                const key = `${x},${y}`
                let tiles = group.cells.get(key)
                if (!tiles) group.cells.set(key, tiles = [])
                tiles.push(bounds)
            }
        }
    }

    overlaps(bounds: Rectangle): boolean {
        this.topLeft.set(bounds.x, bounds.y)
        this.bottomRight.set(bounds.right, bounds.bottom)
        for (const group of this.groups.values()) {
            const { container, cells } = group
            if (!container.worldVisible) continue
            container.toLocal(this.topLeft, undefined, this.localTopLeft)
            container.toLocal(this.bottomRight, undefined, this.localBottomRight)
            if (container.worldAlpha <= 0.15) continue
            const left = Math.min(this.localTopLeft.x, this.localBottomRight.x)
            const top = Math.min(this.localTopLeft.y, this.localBottomRight.y)
            const right = Math.max(this.localTopLeft.x, this.localBottomRight.x)
            const bottom = Math.max(this.localTopLeft.y, this.localBottomRight.y)
            if (left >= group.right || right <= group.left || top >= group.bottom || bottom <= group.top) continue
            for (let x = Math.floor(left / TileSize); x < Math.ceil(right / TileSize); x++) {
                for (let y = Math.floor(top / TileSize); y < Math.ceil(bottom / TileSize); y++) {
                    const tiles = cells.get(`${x},${y}`)
                    if (!tiles) continue
                    for (const tile of tiles) {
                        if (tile.x < right && tile.right > left && tile.y < bottom && tile.bottom > top) return true
                    }
                }
            }
        }
        return false
    }
}
